import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Stack,
  Backdrop,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CardElement,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import axiosInstance from "../../utils/config/config";
import { SecondaryBtn } from "../../Components/Common/CustomBtn";
import { ErrorSnackbar } from "../../Components/Common/CustomSnackbar";

const StripePayment = () => {
  const stripeStyle = {
    base: {
      fontSize: "16px",
      color: "#333",
      "::placeholder": {
        color: "#999",
      },
    },
    invalid: {
      color: "#e5424d",
    },
  };

  const { state } = useLocation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const planMeta = state?.selectedPlan;

  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState({ error: false, message: "" });

  useEffect(() => {
    fetchSavedCards();
  }, []);

  const fetchSavedCards = async () => {
    try {
      const { data } = await axiosInstance.get("/payments/getUserInfo");
      setSavedCards(data.paymentMethods || []);
      const cards = data.paymentMethods || [];
      setAddingNew(cards.length === 0);
    } catch {
      setErrorState({ error: true, message: "Failed to fetch cards." });
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      let paymentMethodId = selectedCardId;

      if (addingNew) {
        const { data: setupIntentRes } = await axiosInstance.post(
          "/payments/createPaymentIntent"
        );

        const result = await stripe.confirmCardSetup(
          setupIntentRes.clientSecret,
          {
            payment_method: {
              card: elements.getElement(CardNumberElement),
            },
          }
        );

        if (result.error) {
          setErrorState({ error: true, message: result.error.message });
          return;
        }

        paymentMethodId = result.setupIntent.payment_method;
      }

      const { data } = await axiosInstance.post(
        "/payments/create-subscription",
        {
          paymentMethodId,
          plan: planMeta?.value,
        }
      );

      if (data.subscriptionId) {
        navigate("/dashboard");
      }
    } catch (err) {
      setErrorState({ error: true, message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const getPlanPrice = () => {
    if (planMeta?.value === "Basic Plan") return "$5";
    if (planMeta?.value === "Growth Plan") return "$15";
    return "";
  };

  return (
    <Box sx={{ p: 3 }}>
      <Backdrop open={loading} sx={{ zIndex: 9999 }}>
        <CircularProgress />
      </Backdrop>

      <ErrorSnackbar
        error={errorState.error}
        errorMsg={errorState.message}
        handleCloseError={() => setErrorState({ error: false, message: "" })}
      />

      <Typography variant="h5" fontWeight="bold" mb={1}>
        Select Payment Method
      </Typography>
      <Typography variant="subtitle1" mb={3}>
        Plan: {planMeta?.value} — Price: {getPlanPrice()}
      </Typography>

      {savedCards.length > 0 && !addingNew && (
        <Stack spacing={2} mb={2}>
          {savedCards.map((card) => (
            <Box
              key={card.id}
              onClick={() => setSelectedCardId(card.id)}
              sx={{
                p: 2,
                borderRadius: 2,
                border:
                  selectedCardId === card.id
                    ? "2px solid #FC0108"
                    : "1px solid #ccc",
                cursor: "pointer",
              }}
            >
              <Typography fontWeight="bold">
                **** **** **** {card.card.last4}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {card.card.brand} — Exp {card.card.exp_month}/
                {card.card.exp_year}
              </Typography>
            </Box>
          ))}
          <SecondaryBtn
            text="Add New Card"
            width="100%"
            onClick={() => {
              setAddingNew(true);
              setSelectedCardId(null);
            }}
            noIcon
          />
        </Stack>
      )}

      {addingNew && (
        <Box mt={3}>
          <Typography variant="body2" mb={1}>
            Enter your card info
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxWidth: 500,
            }}
          >
            <Box sx={{ border: "1px solid #ccc", p: 2, borderRadius: 2 }}>
              <Typography variant="caption">Card Number</Typography>
              <CardNumberElement options={{ style: stripeStyle }} />
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Box
                sx={{
                  border: "1px solid #ccc",
                  p: 2,
                  borderRadius: 2,
                  flex: 1,
                }}
              >
                <Typography variant="caption">Expiry</Typography>
                <CardExpiryElement options={{ style: stripeStyle }} />
              </Box>
              <Box
                sx={{
                  border: "1px solid #ccc",
                  p: 2,
                  borderRadius: 2,
                  flex: 1,
                }}
              >
                <Typography variant="caption">CVC</Typography>
                <CardCvcElement options={{ style: stripeStyle }} />
              </Box>
            </Box>
          </Box>

          {savedCards.length > 0 && (
            <SecondaryBtn
              text="Back to Saved Cards"
              width="100%"
              onClick={() => {
                setAddingNew(false);
                setSelectedCardId(null);
              }}
              noIcon
            />
          )}
        </Box>
      )}

      <Stack direction="row" spacing={2} mt={4}>
        <SecondaryBtn
          text="Cancel"
          width="50%"
          onClick={() => navigate(-1)}
          noIcon
        />
        <SecondaryBtn
          text="Confirm"
          width="50%"
          onClick={handleConfirm}
          filled
          disabled={!stripe || (!selectedCardId && !addingNew)}
          noIcon
        />
      </Stack>
    </Box>
  );
};

export default StripePayment;
